import { useRef, useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { Menu, Sun, Moon } from 'lucide-react';
import {
  addUserMessage,
  addBotMessage,
  setTyping,
  updateMessageFeedback,
} from './store/slices/chatSlice';
import {
  setSidebarOpen,
  setUserId,
  setSessionId,
} from './store/slices/uiSlice';
import ChatSidebar from './components/sidebar/ChatSidebar';
// @ts-ignore - JSX components without type declarations
import ChatMessage from './components/chat/ChatMessage';
// @ts-ignore - JSX components without type declarations
import ChatInput from './components/chat/ChatInput';
import FeedbackModal from './components/chat/FeedbackModal';
import { ToastProvider, useToast } from './components/toast/ToastProvider';
import { HeaderMappingModal } from './components/modals/HeaderMappingModal';
import { ValidationModal } from './components/modals/ValidationModal';
import { LoadModal } from './components/modals/LoadModal';
import { apiService } from './services/apiService';
import { parseExcelFile } from './utils/excelParser';
import { predictColumns } from './services/columnMappingService';
import type {
  ColumnMappingEntry,
  ValidationResult,
  ValidationRecord,
  ValidationIssue,
  LoadResult,
  LoadRecord,
} from './types/rosterFlow';
import { ROSTER_STANDARD_FIELDS } from './types/rosterFlow';
import { generateSessionId } from './constants';
import { sendFeedback, buildFeedbackPayload } from './services/feedbackService';
import { saveUploadMetric } from './services/uploadMetricsService';
import { fetchWithRetry } from './utils/fetchWithRetry';
import UploadDashboard from './components/modals/UploadDashboard';
import UhcLogo from './components/icons/UhcLogo';

export interface FileWithPurpose {
  file: File;
  purpose: string;
  id: string;
  gcsUri?: string;
  fileName?: string;
}

export interface AppProps {
  userId?: string;
  sessionId?: string;
  apiBaseUrl?: string;
  coreApiBaseUrl?: string;
  jobNumber?: string;
  appName?: string;
  onRequestClose?: () => void;
}

const toastRef: { current: ReturnType<typeof useToast> | null } = { current: null };
function ToastBridge() {
  toastRef.current = useToast();
  return null;
}

function App({
  userId: userIdProp,
  apiBaseUrl: apiBaseUrlProp,
  sessionId: sessionIdProp,
}: AppProps = {}): JSX.Element {
  const dispatch = useAppDispatch();
  const [colorMode, setColorMode] = useState<'light' | 'dark'>('light');
  const [hasStartedChat, setHasStartedChat] = useState(false);
  const hasDispatchedWelcomeRef = useRef(false);
  const [filesUploaded, setFilesUploaded] = useState(false);

  // === Roster Workflow Stages State ===
  const [columnMappingData, setColumnMappingData] = useState<{
    columns: string[];
    rows: Record<string, any>[];
    initialMappings?: ColumnMappingEntry[];
  } | null>(null);
  const [showColumnMappingModal, setShowColumnMappingModal] = useState(false);
  const [pendingColumnMappingFiles, setPendingColumnMappingFiles] = useState<FileWithPurpose[]>([]);
  const [pendingParsedRows, setPendingParsedRows] = useState<Record<string, any>[]>([]);
  const [validationData, setValidationData] = useState<ValidationResult | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [pendingValidationResult, setPendingValidationResult] = useState<ValidationResult | null>(null);
  const [loadData, setLoadData] = useState<LoadResult | null>(null);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [isRevalidating, setIsRevalidating] = useState(false);
  const [isColumnMappingLoading, setIsColumnMappingLoading] = useState(false);
  const [isValidationLoading, setIsValidationLoading] = useState(false);
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [validationSubmitted, setValidationSubmitted] = useState(false);
  const [loadSubmitted, setLoadSubmitted] = useState(false);
  const [validationEditedData, setValidationEditedData] = useState<Record<number, Record<string, string>>>({});
  const [awaitingNewSubmission, setAwaitingNewSubmission] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);

  // === Feedback State ===
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [currentFeedbackMessageId, setCurrentFeedbackMessageId] = useState<string | null>(null);
  const [currentFeedbackComment, setCurrentFeedbackComment] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Redux state
  const { messages, isTyping } = useAppSelector(state => state.chat);
  const { isSidebarOpen, userId, sessionId } = useAppSelector(state => state.ui);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  // Initialize user and API config
  useEffect(() => {
    const initUserId = userIdProp || '';
    const initSessionId = sessionIdProp || generateSessionId();
    dispatch(setUserId(initUserId));
    if (!sessionId || sessionIdProp) {
      dispatch(setSessionId(initSessionId));
    }
    const baseUrl = apiBaseUrlProp || '';
    if (baseUrl) {
      apiService.configureApiBaseUrl(baseUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userIdProp, sessionIdProp, apiBaseUrlProp]);

  // Welcome message
  useEffect(() => {
    if (!hasStartedChat) {
      setHasStartedChat(true);
      if (!hasDispatchedWelcomeRef.current) {
        hasDispatchedWelcomeRef.current = true;
        dispatch(addBotMessage({
          id: 'msg_welcome',
          content: 'Welcome to **NOVA - Address Maintainance Intelligence** — upload a roster file to initiate the workflow, and I will guide you through each stage.',
          type: 'assistant' as const,
          bot: true,
          timestamp: new Date().toISOString(),
        }));
      }
    }
  }, [hasStartedChat, dispatch]);

  const handleSendMessage = async (text: string, files?: File[]) => {
    // New submission yes/no
    if (awaitingNewSubmission) {
      setAwaitingNewSubmission(false);
      dispatch(addUserMessage({ content: text.trim() }));
      if (text.trim().toLowerCase() === 'yes') {
        window.location.reload();
      }
      return;
    }

    if (!text.trim() && (!files || files.length === 0)) return;

    const hasFiles = !!(files && files.length > 0);

    if (hasFiles && files) {
      dispatch(addUserMessage({ content: text.trim() || `Uploading ${files.length} file(s)\u2026` }));
      await handleFileUpload(files);
      return;
    }

    // General chat — no orchestrator backend
    dispatch(addUserMessage({ content: text.trim() }));
    dispatch(addBotMessage({
      id: `msg_${Date.now()}`,
      content: "Upload a roster file to begin the workflow.",
      type: 'assistant' as const,
      bot: true,
      timestamp: new Date().toISOString(),
    }));
  };

  const handleFileUpload = async (files: File[]): Promise<boolean> => {
    const excelFiles = files.filter(f => /\.xlsx?$/i.test(f.name));
    const rejectedFiles = files.filter(f => !/\.xlsx?$/i.test(f.name));

    if (rejectedFiles.length > 0) {
      dispatch(addBotMessage({
        id: `msg_${Date.now()}_rejected`,
        content: `Only Excel files (.xlsx / .xls) are accepted. The following file(s) were not processed: ${rejectedFiles.map(f => `**${f.name}**`).join(', ')}.`,
        type: 'assistant' as const,
        bot: true,
        timestamp: new Date().toISOString(),
        metadata: { agentName: 'upload' },
      }));
    }

    if (excelFiles.length === 0) return false;

    const filesWithPurpose: FileWithPurpose[] = excelFiles.map((file, index) => ({
      file,
      purpose: 'Roster',
      id: `file_${Date.now()}_${index}`,
      fileName: file.name,
    }));

    dispatch(addBotMessage({
      id: `msg_${Date.now()}_excel_recv`,
      content: `Received **${filesWithPurpose.map(f => f.file.name).join(', ')}** \u2014 opening column mapping\u2026`,
      type: 'assistant' as const,
      bot: true,
      timestamp: new Date().toISOString(),
      metadata: { agentName: 'upload' },
    }));
    setFilesUploaded(true);
    await handleColumnMapping(filesWithPurpose);
    return true;
  };

  // Roster flow helpers

  const applyColumnMapping = (
    row: Record<string, any>,
    mappings: ColumnMappingEntry[]
  ): Record<string, string> => {
    const mapped: Record<string, string> = {};
    for (const m of mappings) {
      if (m.systemField !== 'IGNORE' && m.sourceCol !== '__NONE__') {
        mapped[m.systemField] = String(row[m.sourceCol] ?? '').trim();
      }
    }
    return mapped;
  };

  const buildAgentPayload = (rowNumber: number, data: Record<string, string>) => {
    const v = (key: string): string | undefined => data[key] || undefined;
    const today = new Date().toISOString().slice(0, 10);
    const seq   = String(rowNumber).padStart(4, '0');

    const billingAddr: Record<string, string> = {};
    if (v('BILLING_ADDRESS_1'))    billingAddr.address_1            = data['BILLING_ADDRESS_1'];
    if (v('BILLING_CITY'))         billingAddr.city                 = data['BILLING_CITY'];
    if (v('BILLING_STATE'))        billingAddr.state                = data['BILLING_STATE'];
    if (v('BILLING_PHONE_NUMBER')) billingAddr.billing_phone_number = data['BILLING_PHONE_NUMBER'];
    if (v('BILLING_ZIP'))          billingAddr.zip                  = data['BILLING_ZIP'];

    const serviceAddr: Record<string, string> = {};
    if (v('ADDRESS_1')) serviceAddr.address_1 = data['ADDRESS_1'];
    if (v('CITY'))      serviceAddr.city      = data['CITY'];
    if (v('STATE'))     serviceAddr.state     = data['STATE'];
    if (v('ZIP_CODE'))  serviceAddr.zip       = data['ZIP_CODE'];
    if (v('PHONE')) {
      serviceAddr.primary_telephone = data['PHONE'];
      const digits = data['PHONE'].replace(/\D/g, '');
      if (digits.length >= 10) serviceAddr.primary_telephone_area_code = digits.slice(0, 3);
    }

    const addressPair: Record<string, any> = {};
    if (Object.keys(billingAddr).length)  addressPair.provider_billing_address = billingAddr;
    if (Object.keys(serviceAddr).length)  addressPair.provider_service_address = serviceAddr;

    const taxIdBlock: Record<string, any> = {};
    if (v('TIN')) taxIdBlock.tax_id = data['TIN'];
    if (Object.keys(addressPair).length) taxIdBlock.address_pairs = [addressPair];

    const practitioner: Record<string, any> = {};
    if (v('FIRST_NAME'))        practitioner.provider_first_name = data['FIRST_NAME'];
    if (v('LAST_NAME'))         practitioner.provider_last_name  = data['LAST_NAME'];
    if (v('NPI'))               practitioner.npi                 = data['NPI'];
    if (v('PRIMARY_DEGREE'))    practitioner.primary_degree      = data['PRIMARY_DEGREE'];
    if (v('PRIMARY_SPECIALTY')) practitioner.primary_speciality  = data['PRIMARY_SPECIALTY'];
    if (Object.keys(taxIdBlock).length) practitioner.tax_id = taxIdBlock;

    return {
      cdcFlag: 'Y',
      correlationIdentifier: `CORR-${Date.now()}-${rowNumber}`,
      header: {
        'PDP_PES.HeaderRec': {
          submittedDate: today,
          trackingId: `TRK-${today}-${seq}`,
        },
      },
      practitioner,
    };
  };

  const callValidationAgent = async (
    rowNumber: number,
    data: Record<string, string>
  ): Promise<ValidationRecord> => {
    const body = buildAgentPayload(rowNumber, data);
    try {
      const res = await fetchWithRetry(
        '/widget-api/api/nova/validation-agent/validate',
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      let json: any;
      try { json = await res.json(); } catch { json = {}; }

      if (!res.ok || json.validation_failure) {
        if (json.validation_failure && Array.isArray(json.errors)) {
          const issues: ValidationIssue[] = json.errors.map((e: any) => ({
            field:    String(e.error_field ?? e.field ?? 'unknown'),
            message:  String(e.message ?? ''),
            severity: 'error' as const,
          }));
          return { rowNumber, data, status: 'error', issues };
        }
        throw new Error(`HTTP ${res.status}`);
      }

      const issues: ValidationIssue[] = (json.issues ?? []).map((iss: any) => ({
        field:    String(iss.field    ?? iss.column ?? 'unknown'),
        message:  String(iss.message ?? iss.reason  ?? ''),
        severity: (iss.severity ?? iss.level ?? 'error') as 'error' | 'warning',
      }));
      const status: 'valid' | 'error' | 'warning' =
        json.status === 'valid' || json.status === 'error' || json.status === 'warning'
          ? json.status
          : issues.some(i => i.severity === 'error') ? 'error'
          : issues.length > 0 ? 'warning' : 'valid';
      return { rowNumber, data, status, issues, enrichedPayload: json };
    } catch (err) {
      return {
        rowNumber,
        data,
        status: 'error',
        issues: [{ field: '_agent', message: `Validation agent unreachable: ${err instanceof Error ? err.message : String(err)}`, severity: 'error' }],
      };
    }
  };

  const runValidationAgent = async (
    rows: Record<string, any>[],
    mappings: ColumnMappingEntry[]
  ): Promise<ValidationResult> => {
    const BATCH = 10;
    const records: ValidationRecord[] = [];
    for (let i = 0; i < rows.length; i += BATCH) {
      const batch = rows.slice(i, i + BATCH);
      const results = await Promise.all(
        batch.map((row, bi) => {
          const rowNumber = (row.__rowNumber as number) || (i + bi + 2);
          const data = applyColumnMapping(row, mappings);
          return callValidationAgent(rowNumber, data);
        })
      );
      records.push(...results);
    }
    return {
      totalRows:   records.length,
      validRows:   records.filter(r => r.status === 'valid').length,
      errorRows:   records.filter(r => r.status === 'error').length,
      warningRows: records.filter(r => r.status === 'warning').length,
      records,
    };
  };

  // Column Mapping

  const handleColumnMapping = async (excelFiles: FileWithPurpose[]) => {
    setValidationSubmitted(false);
    setLoadSubmitted(false);
    setValidationEditedData({});
    setIsColumnMappingLoading(true);
    dispatch(addBotMessage({
      id: `msg_${Date.now()}_cm`,
      content: `Parsing **${excelFiles.map(f => f.file.name).join(', ')}** and running AI column detection\u2026`,
      type: 'assistant' as const,
      bot: true,
      timestamp: new Date().toISOString(),
      metadata: { agentName: 'column-mapping' },
    }));
    dispatch(setTyping(true));
    try {
      const firstFile = excelFiles[0];
      const parsed = await parseExcelFile(firstFile.file);
      const apiMappings = await predictColumns(parsed.columns);

      const getSamples = (col: string, max = 3): string[] => {
        const vals: string[] = [];
        for (const row of parsed.rows) {
          if (vals.length >= max) break;
          const v = row[col];
          if (v !== undefined && v !== null && String(v).trim() !== '')
            vals.push(String(v).trim().slice(0, 28));
        }
        return vals;
      };

      const apiByTarget = new Map(apiMappings.map(m => [m.predicted_target.toUpperCase(), m]));
      const entries: ColumnMappingEntry[] = ROSTER_STANDARD_FIELDS.map((field, i) => {
        const hit = apiByTarget.get(field);
        return {
          id:              `field_${i}`,
          sourceCol:       hit ? hit.source_column : '__NONE__',
          systemField:     field,
          sampleValues:    hit ? getSamples(hit.source_column) : [],
          isAutoMapped:    !!hit,
          confidenceScore: hit?.confidence_score,
          requiresReview:  hit?.requires_review ?? false,
        };
      });

      setPendingParsedRows(parsed.rows);
      setPendingColumnMappingFiles(excelFiles);
      setColumnMappingData({ columns: parsed.columns, rows: parsed.rows, initialMappings: entries });
      setShowColumnMappingModal(true);

      const reviewCount = entries.filter(e => e.requiresReview).length;
      dispatch(addBotMessage({
        id: `msg_${Date.now()}_cm_ready`,
        content: `Found **${parsed.columns.length} column(s)** \u2014 AI mapped **${entries.filter(e => e.isAutoMapped).length}** automatically.${
          reviewCount > 0 ? ` **${reviewCount}** column(s) flagged for manual review.` : ''
        } Check the **Column Mapping** panel and click _Proceed to Validation_ when ready.`,
        type: 'assistant' as const,
        bot: true,
        timestamp: new Date().toISOString(),
        metadata: { agentName: 'column-mapping' },
      }));
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : 'Unknown error';
      setIsColumnMappingLoading(false);
      // Only suggest re-checking the file if the error isn't clearly a server/proxy issue
      const isServerError = errMsg.toLowerCase().includes('proxy') ||
                            errMsg.toLowerCase().includes('html instead of json') ||
                            errMsg.toLowerCase().includes('oauth2') ||
                            errMsg.toLowerCase().includes('502') ||
                            errMsg.toLowerCase().includes('bad gateway');
      const hint = isServerError
        ? ''
        : ' Ensure it is a valid Excel file (.xlsx / .xls) and try again.';
      dispatch(addBotMessage({
        id: `msg_${Date.now()}_cm_err`,
        content: `Could not complete column mapping \u2014 ${errMsg}.${hint}`,
        type: 'assistant' as const,
        bot: true,
        timestamp: new Date().toISOString(),
        metadata: { agentName: 'column-mapping' },
      }));
    } finally {
      setIsColumnMappingLoading(false);
      dispatch(setTyping(false));
    }
  };

  // Re-validation

  const handleRevalidate = async (correctedData: Record<number, Record<string, string>>) => {
    if (!validationData) return;
    setIsRevalidating(true);
    try {
      const BATCH = 10;
      const errorRecords = validationData.records.filter(r => r.status === 'error');
      const passRecords  = validationData.records.filter(r => r.status !== 'error');
      const revalidated: ValidationRecord[] = [];

      for (let i = 0; i < errorRecords.length; i += BATCH) {
        const batch = errorRecords.slice(i, i + BATCH);
        const results = await Promise.all(
          batch.map(rec => {
            const mergedData: Record<string, string> = { ...rec.data, ...(correctedData[rec.rowNumber] ?? {}) };
            return callValidationAgent(rec.rowNumber, mergedData);
          })
        );
        revalidated.push(...results);
      }

      const rowMap = new Map<number, ValidationRecord>();
      [...passRecords, ...revalidated].forEach(r => rowMap.set(r.rowNumber, r));
      const records = validationData.records.map(r => rowMap.get(r.rowNumber) ?? r);

      const errorRows   = records.filter(r => r.status === 'error').length;
      const warningRows = records.filter(r => r.status === 'warning').length;
      const validRows   = records.filter(r => r.status === 'valid').length;
      const newResult: ValidationResult = { totalRows: records.length, validRows, errorRows, warningRows, records };
      setValidationData(newResult);
      setPendingValidationResult(newResult);
    } finally {
      setIsRevalidating(false);
    }
  };

  // Validation

  const handleValidationWithData = async (
    _excelFiles: FileWithPurpose[],
    mappings: ColumnMappingEntry[],
    rows: Record<string, any>[]
  ) => {
    dispatch(setTyping(true));
    dispatch(addBotMessage({
      id: `msg_${Date.now()}_val`,
      content: `Sending **${rows.length} row(s)** to the validation agent for field-level checks\u2026`,
      type: 'assistant' as const,
      bot: true,
      timestamp: new Date().toISOString(),
      metadata: { agentName: 'validation' },
    }));    setIsValidationLoading(true);    try {
      const result = await runValidationAgent(rows, mappings);
      setValidationData(result);
      setPendingValidationResult(result);
      setShowValidationModal(true);
      const summary = result.errorRows > 0
        ? `Validation found **${result.errorRows} error(s)** and **${result.warningRows} warning(s)**. Review them in the Validation panel \u2014 errors must be resolved before loading.`
        : result.warningRows > 0
        ? `Validation passed with **${result.warningRows} warning(s)**. Review them or proceed directly to Load.`
        : `Validation passed \u2014 all **${result.validRows} row(s)** are clean and ready to load.`;
      dispatch(addBotMessage({
        id: `msg_${Date.now()}_val_done`,
        content: summary,
        type: 'assistant' as const,
        bot: true,
        timestamp: new Date().toISOString(),
        metadata: { agentName: 'validation' },
      }));
    } catch (error) {
      setIsValidationLoading(false);
      dispatch(addBotMessage({
        id: `msg_${Date.now()}_val_err`,
        content: `Validation failed \u2014 ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
        type: 'assistant' as const,
        bot: true,
        timestamp: new Date().toISOString(),
        metadata: { agentName: 'validation' },
      }));
    } finally {
      setIsValidationLoading(false);
      dispatch(setTyping(false));
    }
  };

  // Load

  const callLoadAgent = async (
    rowNumber: number,
    data: Record<string, string>,
    enrichedPayload?: Record<string, any>
  ): Promise<LoadRecord> => {
    const body = enrichedPayload?.payload ?? buildAgentPayload(rowNumber, data);
    try {
      const res = await fetchWithRetry(
        '/widget-api/api/nova/load-agent/transform',
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      let json: any;
      try { json = await res.json(); } catch { json = {}; }
      if (!res.ok) {
        const msg = json.message ?? json.error ?? `HTTP ${res.status}`;
        return { rowNumber, memberId: data['NPI'] ?? String(rowNumber), status: 'failed', message: String(msg) };
      }
      const responseMsg = json.message ? String(json.message) : '';
      const isSuccess = responseMsg.toUpperCase().includes('SUCCESS');
      return {
        rowNumber,
        memberId: String(json.memberId ?? json.member_id ?? data['NPI'] ?? rowNumber),
        status: isSuccess ? 'loaded' : 'failed',
        message: responseMsg || undefined,
      };
    } catch (err) {
      return {
        rowNumber,
        memberId: data['NPI'] ?? String(rowNumber),
        status: 'failed',
        message: `Load agent unreachable: ${err instanceof Error ? err.message : String(err)}`,
      };
    }
  };

  const handleLoadWithData = async (
    _excelFiles: FileWithPurpose[],
    validationResult: ValidationResult
  ) => {
    dispatch(setTyping(true));
    setIsLoadLoading(true);
    const eligibleRecords = validationResult.records.filter(r => r.status !== 'error');
    dispatch(addBotMessage({
      id: `msg_${Date.now()}_load`,
      content: `Sending **${eligibleRecords.length} record(s)** to the NDB load agent\u2026`,
      type: 'assistant' as const,
      bot: true,
      timestamp: new Date().toISOString(),
      metadata: { agentName: 'load' },
    }));
    const startMs = Date.now();
    try {
      const BATCH = 10;
      const loadRecords: LoadRecord[] = [];
      for (let i = 0; i < eligibleRecords.length; i += BATCH) {
        const batch = eligibleRecords.slice(i, i + BATCH);
        const results = await Promise.all(
          batch.map(rec => callLoadAgent(rec.rowNumber, rec.data, rec.enrichedPayload))
        );
        loadRecords.push(...results);
      }
      const loadedRecords  = loadRecords.filter(r => r.status === 'loaded').length;
      const failedRecords  = loadRecords.filter(r => r.status === 'failed').length;
      const skippedRecords = loadRecords.filter(r => r.status === 'skipped').length;
      const result: LoadResult = {
        totalRecords: loadRecords.length,
        loadedRecords,
        failedRecords,
        skippedRecords,
        durationMs: Date.now() - startMs,
        records: loadRecords,
      };
      setLoadData(result);
      setShowLoadModal(true);
      // Save metrics for the upload dashboard
      saveUploadMetric({
        fileName: _excelFiles[0]?.file.name ?? 'unknown.xlsx',
        uploadedAt: new Date().toISOString(),
        totalRows: validationResult.totalRows,
        validRows: validationResult.validRows,
        errorRows: validationResult.errorRows,
        warningRows: validationResult.warningRows,
        totalRecords: result.totalRecords,
        loadedRecords: result.loadedRecords,
        failedRecords: result.failedRecords,
        skippedRecords: result.skippedRecords,
        durationMs: result.durationMs,
        failedDetails: result.records
          .filter(r => r.status === 'failed')
          .map(r => ({ rowNumber: r.rowNumber, memberId: r.memberId, message: r.message })),
      });
      const summary = failedRecords === 0
        ? `**Load complete** \u2014 all **${loadedRecords} record(s)** ingested into NDB successfully in ${(result.durationMs / 1000).toFixed(1)}s.`
        : `**Load finished** \u2014 ${loadedRecords} record(s) loaded, ${failedRecords} failed. Review the Load panel for details.`;
      dispatch(addBotMessage({
        id: `msg_${Date.now()}_load_done`,
        content: summary,
        type: 'assistant' as const,
        bot: true,
        timestamp: new Date().toISOString(),
        metadata: { agentName: 'load' },
      }));
    } finally {
      setIsLoadLoading(false);
      dispatch(setTyping(false));
    }
  };

  // Feedback

  const handleFeedbackLike = useCallback((messageId: string) => {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg) return;
    const newReaction = msg.metadata?.reaction === 'like' ? '' : 'like';
    dispatch(updateMessageFeedback({ messageId, reaction: newReaction as 'like' | 'dislike' | '' }));
    if (newReaction === 'like') {
      const payload = buildFeedbackPayload(msg, userId || '', sessionId || '', '', 'like', msg.metadata?.comment, false);
      sendFeedback(payload)
        .then((res) => {
          if (res.success) toastRef.current?.show('feedback-like', 'success', 'Feedback Submitted', 'Thank you!', 3000);
          else toastRef.current?.show('feedback-error', 'error', 'Feedback Failed', res.error || 'Could not submit feedback.', 4000);
        })
        .catch(() => toastRef.current?.show('feedback-error', 'error', 'Feedback Failed', 'Could not submit feedback.', 4000));
    }
  }, [messages, dispatch, sessionId, userId]);

  const handleFeedbackDislike = useCallback((messageId: string) => {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg) return;
    const newReaction = msg.metadata?.reaction === 'dislike' ? '' : 'dislike';
    dispatch(updateMessageFeedback({ messageId, reaction: newReaction as 'like' | 'dislike' | '' }));
    if (newReaction === 'dislike') {
      const payload = buildFeedbackPayload(msg, userId || '', sessionId || '', '', 'dislike', msg.metadata?.comment, false);
      sendFeedback(payload)
        .then((res) => {
          if (res.success) toastRef.current?.show('feedback-dislike', 'success', 'Feedback Submitted', 'Thank you!', 3000);
          else toastRef.current?.show('feedback-error', 'error', 'Feedback Failed', res.error || 'Could not submit feedback.', 4000);
        })
        .catch(() => toastRef.current?.show('feedback-error', 'error', 'Feedback Failed', 'Could not submit feedback.', 4000));
    }
  }, [messages, dispatch, sessionId, userId]);

  const handleFeedbackComment = useCallback((messageId: string) => {
    const msg = messages.find((m) => m.id === messageId);
    setCurrentFeedbackMessageId(messageId);
    setCurrentFeedbackComment(msg?.metadata?.comment || '');
    setFeedbackModalOpen(true);
  }, [messages]);

  const handleFeedbackCommentSubmit = useCallback((comment: string) => {
    if (!currentFeedbackMessageId) return;
    const msg = messages.find((m) => m.id === currentFeedbackMessageId);
    dispatch(updateMessageFeedback({ messageId: currentFeedbackMessageId, comment }));
    const reaction = msg?.metadata?.reaction || '';
    const payload = buildFeedbackPayload(msg || { content: '' }, userId || '', sessionId || '', '', reaction as 'like' | 'dislike' | '', comment, false);
    sendFeedback(payload)
      .then((res) => {
        if (res.success) toastRef.current?.show('feedback-comment', 'success', 'Comment Submitted', 'Your feedback has been recorded.', 3000);
        else toastRef.current?.show('feedback-error', 'error', 'Feedback Failed', res.error || 'Could not submit comment.', 4000);
      })
      .catch(() => toastRef.current?.show('feedback-error', 'error', 'Feedback Failed', 'Could not submit comment.', 4000));
  }, [currentFeedbackMessageId, messages, dispatch, sessionId, userId]);

  const handleFeedbackModalClose = useCallback(() => {
    setFeedbackModalOpen(false);
    setCurrentFeedbackMessageId(null);
    setCurrentFeedbackComment('');
  }, []);

  const toggleColorMode = () => setColorMode(prev => prev === 'light' ? 'dark' : 'light');
  const [isMaximized, setIsMaximized] = useState(false);

  return (
    <ToastProvider>
      <ToastBridge />
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.45)',
        backdropFilter: 'blur(6px)',
        zIndex: 9000,
        padding: isMaximized ? '0' : '32px',
        transition: 'padding 0.25s ease',
      }}>
        <div style={{
          display: 'flex', flexDirection: 'column',
          width: '100%', height: '100%',
          maxWidth: isMaximized ? 'none' : 'calc(100% - 64px)',
          maxHeight: isMaximized ? 'none' : 'calc(100% - 64px)',
          borderRadius: isMaximized ? '0' : '16px',
          overflow: 'hidden',
          boxShadow: isMaximized ? 'none' : '0 8px 40px rgba(0,0,0,0.3), 0 2px 12px rgba(0,0,0,0.15)',
          transition: 'all 0.25s ease',
        }}>
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0, background: colorMode === 'dark' ? '#0f172a' : '#f7f8fa' }}>
            {/* Sidebar */}
            {isSidebarOpen && (
              <ChatSidebar
                colorMode={colorMode}
                onNewChat={() => window.location.reload()}
                columnMappingData={columnMappingData}
                validationData={validationData}
                loadData={loadData}
                onColumnMappingClick={() => setShowColumnMappingModal(true)}
                onValidationClick={() => setShowValidationModal(true)}
                onLoadClick={() => setShowLoadModal(true)}
                onDashboardClick={() => setShowDashboard(true)}
                completedStages={[validationSubmitted, loadSubmitted, !!loadData]}
                isColumnMappingLoading={isColumnMappingLoading}
                isValidationLoading={isValidationLoading}
                isLoadLoading={isLoadLoading}
              />
            )}

            {/* Main Area */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, height: '100%' }}>
              {/* Top Navbar */}
              <header style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                height: '56px', padding: '0 20px', flexShrink: 0,
                background: '#002677', color: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button onClick={() => dispatch(setSidebarOpen(!isSidebarOpen))} style={{
                    background: 'none', border: 'none', color: '#fff', cursor: 'pointer',
                    padding: '6px', borderRadius: '8px', display: 'flex', alignItems: 'center',
                  }}>
                    <Menu size={22} />
                  </button>
                  <span><UhcLogo style={{
                    width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                    marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgb(255, 255, 255)', padding: '6px',
                    border: '1.5px solid rgb(224, 227, 234)',
                    boxShadow: 'rgba(0, 38, 119, 0.1) 0px 2px 10px',
                  }} size={22} /></span>
                  <span style={{ fontSize: '17px', fontWeight: 600, letterSpacing: '-0.01em' }}>NOVA - Address Maintainance Intelligence</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button onClick={toggleColorMode} style={{
                    background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', cursor: 'pointer',
                    padding: '7px', borderRadius: '8px', display: 'flex', alignItems: 'center',
                  }}>
                    {colorMode === 'light' ? <Moon size={18} /> : <Sun size={18} />}
                  </button>
                  <button onClick={() => setIsMaximized(prev => !prev)} title={isMaximized ? 'Restore' : 'Maximize'} style={{
                    background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', cursor: 'pointer',
                    padding: '7px', borderRadius: '8px', display: 'flex', alignItems: 'center', fontSize: '16px',
                  }}>
                    {isMaximized ? '\u2750' : '\u2b1c'}
                  </button>
                </div>
              </header>

              {/* Chat Messages */}
              <div style={{ flex: 1, overflowY: 'auto', position: 'relative', minHeight: 0 }}>
                <div style={{
                  width: '100%', minHeight: '100%',
                  display: 'flex', flexDirection: 'column',
                  justifyContent: 'flex-end',
                  padding: '0 0 32px',
                }}>
                  {messages.map(message => (
                    <ChatMessage
                      key={message.id}
                      message={{
                        ...message,
                        sender: message.type === 'user' ? 'user' : 'assistant',
                        text: message.content,
                        timestamp: message.timestamp,
                      }}
                      colorMode={colorMode}
                      onContinueFromHistory={() => {}}

                      onFeedbackLike={handleFeedbackLike}
                      onFeedbackDislike={handleFeedbackDislike}
                      onFeedbackComment={handleFeedbackComment}
                      onSendMessage={handleSendMessage}
                    />
                  ))}

                  {isTyping && (
                    <>
                      <style>{`
                        @keyframes robotBounce {
                          0% { transform: translateY(0px); }
                          50% { transform: translateY(-10px); }
                          100% { transform: translateY(0px); }
                        }
                      `}</style>
                      <div style={{ padding: '10px 24px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          marginTop: '2px', background: '#fff', padding: '6px',
                          border: '1.5px solid #e0e3ea',
                          boxShadow: '0 2px 10px rgba(0,38,119,0.10)',
                        }}>
                          <UhcLogo size={28} />
                        </div>
                        <div style={{
                          background: colorMode === 'dark' ? '#1e1e2e' : '#fafbff',
                          border: colorMode === 'dark' ? '1px solid #2d2d3f' : '1px solid #e2e4f0',
                          borderRadius: '18px 4px 18px 4px', padding: '14px 20px',
                          boxShadow: '0 2px 10px rgba(99,102,241,0.06)',
                          display: 'flex', alignItems: 'center',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontSize: '24px', margin: '0 4px', animation: 'robotBounce 0.6s ease-in-out infinite', animationDelay: '0s' }}>🤖</span>
                            <span style={{ fontSize: '24px', margin: '0 4px', animation: 'robotBounce 0.6s ease-in-out infinite', animationDelay: '0.2s' }}>🤖</span>
                            <span style={{ fontSize: '24px', margin: '0 4px', animation: 'robotBounce 0.6s ease-in-out infinite', animationDelay: '0.4s' }}>🤖</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Chat Input */}
              <ChatInput
                onSendMessage={handleSendMessage}
                colorMode={colorMode}
                disableClip={false}
                hasFiles={filesUploaded}
                disabled={false}
              />
            </div>

            {/* Modals */}
            {showColumnMappingModal && columnMappingData && (
              <HeaderMappingModal
                isOpen={showColumnMappingModal}
                columns={columnMappingData.columns}
                rows={columnMappingData.rows}
                initialMappings={columnMappingData.initialMappings}
                fileName={pendingColumnMappingFiles[0]?.file.name ?? ''}
                submitted={validationSubmitted}
                completedSteps={[validationSubmitted, loadSubmitted, !!loadData]}
                onNavigate={(step) => {
                  setShowColumnMappingModal(false);
                  setShowValidationModal(false);
                  setShowLoadModal(false);
                  if (step === 0 && columnMappingData) setShowColumnMappingModal(true);
                  if (step === 1 && validationData) setShowValidationModal(true);
                  if (step === 2 && loadData) setShowLoadModal(true);
                }}
                onClose={() => setShowColumnMappingModal(false)}
                onConfirm={(mappings) => {
                  setValidationSubmitted(true);
                  setShowColumnMappingModal(false);
                  handleValidationWithData(pendingColumnMappingFiles, mappings, pendingParsedRows);
                }}
              />
            )}
            {showValidationModal && validationData && (
              <ValidationModal
                isOpen={showValidationModal}
                data={validationData}
                fileName={pendingColumnMappingFiles[0]?.file.name ?? ''}
                submitted={loadSubmitted}
                completedSteps={[validationSubmitted, loadSubmitted, !!loadData]}
                onNavigate={(step) => {
                  setShowColumnMappingModal(false);
                  setShowValidationModal(false);
                  setShowLoadModal(false);
                  if (step === 0 && columnMappingData) setShowColumnMappingModal(true);
                  if (step === 1 && validationData) setShowValidationModal(true);
                  if (step === 2 && loadData) setShowLoadModal(true);
                }}
                editedData={validationEditedData}
                onEditedDataChange={setValidationEditedData}
                onConfirm={() => {
                  setLoadSubmitted(true);
                  setShowValidationModal(false);
                  if (pendingValidationResult) {
                    handleLoadWithData(pendingColumnMappingFiles, pendingValidationResult);
                  }
                }}
                onRevalidate={handleRevalidate}
                isRevalidating={isRevalidating}
                onClose={() => setShowValidationModal(false)}
              />
            )}
            {showLoadModal && loadData && (
              <LoadModal
                isOpen={showLoadModal}
                data={loadData}
                fileName={pendingColumnMappingFiles[0]?.file.name ?? ''}
                onClose={() => setShowLoadModal(false)}
                onDone={() => {
                  setAwaitingNewSubmission(true);
                  dispatch(addBotMessage({
                    id: `msg_${Date.now()}_done`,
                    content: 'Thank you for using **NOVA - Address Maintainance Intelligence**! 🎉\n\nWould you like to make a new submission?',
                    type: 'assistant',
                    bot: true,
                    timestamp: new Date().toISOString(),
                    metadata: { suggestions: ['Yes', 'No'] },
                  }));
                }}
                completedSteps={[validationSubmitted, loadSubmitted, !!loadData]}
                onNavigate={(step) => {
                  setShowColumnMappingModal(false);
                  setShowValidationModal(false);
                  setShowLoadModal(false);
                  if (step === 0 && columnMappingData) setShowColumnMappingModal(true);
                  if (step === 1 && validationData) setShowValidationModal(true);
                  if (step === 2 && loadData) setShowLoadModal(true);
                }}
              />
            )}
            <FeedbackModal
              isOpen={feedbackModalOpen}
              onClose={handleFeedbackModalClose}
              onSubmit={handleFeedbackCommentSubmit}
              initialComment={currentFeedbackComment}
              isDark={colorMode === 'dark'}
            />
            <UploadDashboard
              isOpen={showDashboard}
              onClose={() => setShowDashboard(false)}
              colorMode={colorMode}
            />
          </div>
        </div>
      </div>
    </ToastProvider>
  );
}

export default App;
